// Data model for the "big" Schadenmeldung wizard. Flat shape so the wizard
// shell can keep using update(patch). Single-choice fields are string | null,
// multi-choice are string[], free text is string.

export type BigDamageType = "gebaeude" | "kfz" | "sonstiges";

export interface BigStepProps {
  answers: BigAnswers;
  update: (patch: Partial<BigAnswers>) => void;
}

export function toggleValue(arr: string[], id: string): string[] {
  return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
}

// Einheitlicher Kontaktblock (Name gesplittet, mit Handy, optional Anschrift/
// Funktion/Kürzel). Wird überall für Ansprechpartner, Mieter, Verursacher,
// Fahrer, Halter, Zeugen, Betroffene etc. verwendet.
export interface Contact {
  vorname: string;
  name: string;
  strasse: string;
  plz: string;
  ort: string;
  tel: string;
  handy: string;
  email: string;
  funktion: string;
  kuerzel: string;
}

export const emptyContact: Contact = {
  vorname: "",
  name: "",
  strasse: "",
  plz: "",
  ort: "",
  tel: "",
  handy: "",
  email: "",
  funktion: "",
  kuerzel: "",
};

// True if the contact has any meaningful content.
export function contactFilled(c: Contact): boolean {
  return Object.values(c).some((v) => v.trim().length > 0);
}

// Eine betroffene Gebäude-Einheit (Tabelle statt Haus-Grafik): Etage + Lage(n) +
// vermietet + Zusatzangaben, mit eindeutiger Zuordnung (z. B. „OG3 Mitte Vorne").
export interface GebEinheit {
  etage: string; // keller | eg | og1 … | dach | sonstige
  lage: string[]; // links | mitte | rechts | vorne | hinten
  vermietet: string | null; // ja | nein
  zusatz: string;
}

export function makeEinheit(etage: string): GebEinheit {
  return { etage, lage: [], vermietet: null, zusatz: "" };
}

// Eine verletzte Person (wiederholbar: „Gibt es weitere verletzte Personen?").
export interface InjuredPerson {
  name: string;
  rolle: string | null; // fahrer | beifahrer | insasse | sonstiges (nur KFZ)
  rolleDetail: string;
  zugehoerig: string | null; // ja | nein | unbekannt
  verletzung: string;
  arbeitsunfaehig: string | null; // ja | nein | unbekannt
  unterlagen: string | null; // ja | nein (ärztliche Unterlagen)
}

export function makeInjuredPerson(): InjuredPerson {
  return {
    name: "",
    rolle: null,
    rolleDetail: "",
    zugehoerig: null,
    verletzung: "",
    arbeitsunfaehig: null,
    unterlagen: null,
  };
}

// Ein fremdes (gegnerisches) Fahrzeug — wiederholbar („Gibt es ein weiteres
// fremdes betroffenes Fahrzeug?").
export interface KfzFremd {
  kennzeichen: string;
  art: string;
  hersteller: string;
  farbe: string;
  anhaenger: string | null;
  fahrbereit: string | null;
  fahrer: Contact;
  halter: Contact;
  versichererBekannt: string | null;
  versicherer: string;
  vsnr: string;
  schadennr: string;
  ansprech: string;
  vorsteuer: string | null;
  zonen: string[];
  abgeschleppt: string | null;
  hoehe: string;
  kv: string | null;
  mietwagen: string | null;
  mietwagenDetail: string;
}

export function makeKfzFremd(): KfzFremd {
  return {
    kennzeichen: "",
    art: "",
    hersteller: "",
    farbe: "",
    anhaenger: null,
    fahrbereit: null,
    fahrer: { ...emptyContact },
    halter: { ...emptyContact },
    versichererBekannt: null,
    versicherer: "",
    vsnr: "",
    schadennr: "",
    ansprech: "",
    vorsteuer: null,
    zonen: [],
    abgeschleppt: null,
    hoehe: "",
    kv: null,
    mietwagen: null,
    mietwagenDetail: "",
  };
}

// Zusätzliche betroffene Person (Sonstiges): Kontaktblock + Verletzung.
export interface ExtraPerson {
  contact: Contact;
  verletzung: string;
}

export function makeExtraPerson(): ExtraPerson {
  return { contact: { ...emptyContact }, verletzung: "" };
}

// Generischer Helfer für wiederholbare Listen: Element an Index patchen.
export function patchAt<T>(arr: T[], index: number, patch: Partial<T>): T[] {
  return arr.map((item, i) => (i === index ? { ...item, ...patch } : item));
}

export interface BigAnswers {
  damageType: BigDamageType | null;

  // --- shared: Schadenmelder -------------------------------------------------
  melderVorname: string;
  melderName: string;
  melderTel: string;
  melderHandy: string;
  melderEmail: string;
  melderKuerzel: string;
  melderFuerUnternehmen: string;
  melderVorgangsnummer: string;

  // --- shared: Ansprechpartner ----------------------------------------------
  apWer: string | null; // "melder" | "andere"
  apContact: Contact;

  // --- shared: Schadentag ----------------------------------------------------
  schadenDatum: string; // datetime-local
  schadenZeitpunkt: string; // freitext "ungefährer Zeitpunkt"
  entdecktAm: string;

  // ===========================================================================
  // GEBÄUDE
  // ===========================================================================
  gebPlz: string;
  gebOrt: string;
  gebStrasse: string;
  gebObjektNr: string;
  gebWohneinheit: string; // Wohnung / Einheit-Nr. (falls vergeben)
  gebEinheiten: GebEinheit[]; // betroffene Einheiten (Tabelle)
  gebSonstigeAngaben: string; // Sonstige Angaben zur Einheit
  gebMieterContact: Contact;
  gebMieterHausrat: string | null; // "ja" | "nein"
  gebMieterVersicherer: string;
  gebMieterVsnr: string; // Versicherungsscheinnummer (split aus Versicherer)
  gebBeschaedigt: string[]; // Einzelantennen / Markisen / Verglasung / Tapeten-Böden / Sonstiges

  gebSchadenart: string | null; // feuer | leitungswasser | sturm_hagel | sonstige
  gebUntergefahr: string | null; // zweite Ebene, abhängig von gebSchadenart
  gebUntergefahrDetail: string; // Freitext bei "sonstiges"

  gebHergang: string;
  gebSchadenstelle: string;
  gebUrsache: string | null; // "bekannt" | "suchen"
  gebFolgeschaeden: string;
  gebBetroffeneTeile: string;
  gebSchadenhoehe: string;
  gebNutzungMoeglich: string | null; // ja | nein
  gebNutzungDetail: string; // Freitext bei "nein"

  gebMassnahmeBeauftragt: string | null; // ja | nein
  gebMassnahmeWas: string;
  gebFirma: string;
  gebAuftragsnummer: string;
  gebKostenvoranschlag: string | null; // ja | nein
  gebRechnungen: string | null; // ja | nein

  gebSanierung: string | null; // nein | ja | unsicher

  gebVerursacher: string | null; // nein | ja | unsicher
  gebVerursacherContact: Contact;
  gebVerursacherHaftpflicht: string | null; // ja | nein
  gebVerursacherVersicherer: string;
  gebVerursacherVsnr: string;

  gebPolizei: string | null; // nein | ja | unbekannt
  gebPolizeiAktenzeichen: string;
  gebPolizeiDienststelle: string;

  gebWeitere: string | null; // ja | nein
  gebWeitereContact: Contact;
  gebWeitereArt: string; // "um welche Art von Schaden handelt es sich" (Freitext)

  gebVorsteuer: string | null; // nein | ja | unbekannt
  gebVorsteuerAnteil: string;

  gebZahlungEmpfaenger: string | null; // firma | sonstige
  gebZahlungName: string;
  gebIban: string;
  gebBic: string;

  gebZusatz: string;
  gebUnterlagen: string[];

  // ===========================================================================
  // KFZ
  // ===========================================================================
  kfzSchaeden: string[]; // eigenes | fremdes | person | sonstige

  kfzOrt: string;

  kfzArt: string | null; // see KFZ_ART options
  kfzUnfallTyp: string | null; // wenn Unfall mit anderem Fahrzeug
  kfzOhneFremdTyp: string | null; // wenn Schaden ohne anderes Fahrzeug

  // eigenes Fahrzeug
  kfzEigenKennzeichen: string;
  kfzEigenArt: string;
  kfzEigenKm: string;
  kfzEigenAnhaenger: string | null; // ja | nein
  kfzEigenAnhaengerKz: string;
  kfzEigenFahrer: Contact;
  kfzEigenZonen: string[]; // CarSchematic
  kfzEigenAbgeschleppt: string | null; // ja | nein
  kfzEigenHoehe: string;
  kfzEigenWerkstatt: string | null; // ja | nein
  kfzEigenGutachter: string | null; // ja | nein

  // fremde Fahrzeuge / Unfallgegner (wiederholbar)
  kfzFremdFahrzeuge: KfzFremd[];

  // Polizeiliche Meldung (KFZ) — Block aus Gebäude übernommen, ohne Hinweisfeld
  kfzPolizei: string | null; // nein | ja | unbekannt
  kfzPolizeiAktenzeichen: string;
  kfzPolizeiDienststelle: string;

  // Hergang / Zeugen / Skizze
  kfzEinschaetzung: string | null; // wir | gegner | beide | unbekannt | keine_beurteilung
  kfzZeugen: string | null; // ja | nein | unbekannt
  kfzZeugenContact: Contact;
  kfzZeugenAussage: string;
  kfzSkizze: string[]; // europ_bericht | eigene_skizze | keine | nachreichen

  // Personenschaden (wiederholbar)
  kfzPersonen: InjuredPerson[];

  // Sonstige Sachen / Gegenstände
  kfzSachenBeschreibung: string;
  kfzSachenEigentuemer: string | null; // uns | fremd | unbekannt
  kfzSachenFremdAngaben: string;
  kfzSachenHoehe: string;
  kfzSachenHergang: string;
  kfzSachenBelege: string[];
  kfzSachenVorsteuer: string | null; // ja | nein | unbekannt

  kfzUnterlagen: string[];

  // ===========================================================================
  // SONSTIGES
  // ===========================================================================
  sonBetroffen: string | null; // mensch | sache | sonstiges

  sonPlz: string;
  sonOrt: string;
  sonStrasse: string;

  // Sache
  sonSacheBeschreibung: string;
  sonSacheEigentuemer: string | null; // uns | fremd
  sonSacheFremdContact: Contact;
  sonSacheOrt: string;
  sonSacheHergang: string;
  sonSacheHoehe: string;
  sonSacheZeugen: string | null; // ja | nein | unbekannt
  sonSacheZeugenContact: Contact;
  sonSacheBelege: string[];

  // Mensch
  sonMenschWer: string | null; // ja (eigenes Unternehmen) | nein (dritte/fremde Person) | unbekannt
  sonMenschRolle: string; // Rolle der dritten/fremden Person (Freitext)
  sonMenschContact: Contact;
  sonMenschHergang: string;
  sonMenschVerletzung: string;
  sonMenschArbeitsunfaehig: string | null; // ja | nein | unbekannt
  sonMenschWeitere: ExtraPerson[]; // weitere verletzte Personen
  sonMenschZeugen: string | null; // ja | nein | unbekannt
  sonMenschZeugenContact: Contact;

  // Sonstiges
  sonSonstHergang: string;
  sonSonstWer: string | null; // uns | kunde | fremd
  sonSonstBetroffenContact: Contact;
  sonSonstHoehe: string; // geschätzte Schadenhöhe (neue Frage D5)
  sonSonstAnspruch: string | null; // ja | nein | unbekannt
  sonSonstZeugen: string | null; // ja | nein | unbekannt
  sonSonstZeugenContact: Contact;

  sonUnterlagen: string[];

  // --- closing ---------------------------------------------------------------
  kopie: string | null; // mail | download | beides
  consentGiven: boolean;
}

export const initialBigAnswers: BigAnswers = {
  damageType: null,

  melderVorname: "",
  melderName: "",
  melderTel: "",
  melderHandy: "",
  melderEmail: "",
  melderKuerzel: "",
  melderFuerUnternehmen: "",
  melderVorgangsnummer: "",

  apWer: "melder",
  apContact: { ...emptyContact },

  schadenDatum: "",
  schadenZeitpunkt: "",
  entdecktAm: "",

  gebPlz: "",
  gebOrt: "",
  gebStrasse: "",
  gebObjektNr: "",
  gebWohneinheit: "",
  gebEinheiten: [],
  gebSonstigeAngaben: "",
  gebMieterContact: { ...emptyContact },
  gebMieterHausrat: null,
  gebMieterVersicherer: "",
  gebMieterVsnr: "",
  gebBeschaedigt: [],

  gebSchadenart: null,
  gebUntergefahr: null,
  gebUntergefahrDetail: "",

  gebHergang: "",
  gebSchadenstelle: "",
  gebUrsache: null,
  gebFolgeschaeden: "",
  gebBetroffeneTeile: "",
  gebSchadenhoehe: "",
  gebNutzungMoeglich: null,
  gebNutzungDetail: "",

  gebMassnahmeBeauftragt: null,
  gebMassnahmeWas: "",
  gebFirma: "",
  gebAuftragsnummer: "",
  gebKostenvoranschlag: null,
  gebRechnungen: null,

  gebSanierung: null,

  gebVerursacher: null,
  gebVerursacherContact: { ...emptyContact },
  gebVerursacherHaftpflicht: null,
  gebVerursacherVersicherer: "",
  gebVerursacherVsnr: "",

  gebPolizei: null,
  gebPolizeiAktenzeichen: "",
  gebPolizeiDienststelle: "",

  gebWeitere: null,
  gebWeitereContact: { ...emptyContact },
  gebWeitereArt: "",

  gebVorsteuer: null,
  gebVorsteuerAnteil: "",

  gebZahlungEmpfaenger: null,
  gebZahlungName: "",
  gebIban: "",
  gebBic: "",

  gebZusatz: "",
  gebUnterlagen: [],

  kfzSchaeden: [],

  kfzOrt: "",

  kfzArt: null,
  kfzUnfallTyp: null,
  kfzOhneFremdTyp: null,

  kfzEigenKennzeichen: "",
  kfzEigenArt: "",
  kfzEigenKm: "",
  kfzEigenAnhaenger: null,
  kfzEigenAnhaengerKz: "",
  kfzEigenFahrer: { ...emptyContact },
  kfzEigenZonen: [],
  kfzEigenAbgeschleppt: null,
  kfzEigenHoehe: "",
  kfzEigenWerkstatt: null,
  kfzEigenGutachter: null,

  kfzFremdFahrzeuge: [makeKfzFremd()],

  kfzPolizei: null,
  kfzPolizeiAktenzeichen: "",
  kfzPolizeiDienststelle: "",

  kfzEinschaetzung: null,
  kfzZeugen: null,
  kfzZeugenContact: { ...emptyContact },
  kfzZeugenAussage: "",
  kfzSkizze: [],

  kfzPersonen: [makeInjuredPerson()],

  kfzSachenBeschreibung: "",
  kfzSachenEigentuemer: null,
  kfzSachenFremdAngaben: "",
  kfzSachenHoehe: "",
  kfzSachenHergang: "",
  kfzSachenBelege: [],
  kfzSachenVorsteuer: null,

  kfzUnterlagen: [],

  sonBetroffen: null,

  sonPlz: "",
  sonOrt: "",
  sonStrasse: "",

  sonSacheBeschreibung: "",
  sonSacheEigentuemer: null,
  sonSacheFremdContact: { ...emptyContact },
  sonSacheOrt: "",
  sonSacheHergang: "",
  sonSacheHoehe: "",
  sonSacheZeugen: null,
  sonSacheZeugenContact: { ...emptyContact },
  sonSacheBelege: [],

  sonMenschWer: null,
  sonMenschRolle: "",
  sonMenschContact: { ...emptyContact },
  sonMenschHergang: "",
  sonMenschVerletzung: "",
  sonMenschArbeitsunfaehig: null,
  sonMenschWeitere: [],
  sonMenschZeugen: null,
  sonMenschZeugenContact: { ...emptyContact },

  sonSonstHergang: "",
  sonSonstWer: null,
  sonSonstBetroffenContact: { ...emptyContact },
  sonSonstHoehe: "",
  sonSonstAnspruch: null,
  sonSonstZeugen: null,
  sonSonstZeugenContact: { ...emptyContact },

  sonUnterlagen: [],

  kopie: null,
  consentGiven: false,
};

// Step keys cover all three branches; the wizard builds an ordered subset.
export type BigStepKey =
  | "type"
  // nur Schadenmappe (intern): eigene Schritte vor bzw. nach der Schadenart;
  // der öffentliche Wizard (`schrittListe`) liefert sie nie.
  | "kontakt"
  | "risiko"
  // gebäude
  | "geb_melder"
  | "geb_when"
  | "geb_where"
  | "geb_art"
  | "geb_hergang"
  | "geb_massnahmen"
  | "geb_verursacher"
  | "geb_abrechnung"
  | "geb_unterlagen"
  // kfz
  | "kfz_schaeden"
  | "kfz_melder"
  | "kfz_when"
  | "kfz_where"
  | "kfz_art"
  | "kfz_eigenes"
  | "kfz_fremdes"
  | "kfz_hergang"
  | "kfz_person"
  | "kfz_sachen"
  | "kfz_unterlagen"
  // sonstiges
  | "son_betroffen"
  | "son_melder"
  | "son_when"
  | "son_where"
  | "son_details"
  | "son_unterlagen"
  // closing
  | "abschluss"
  | "summary"
  | "done";
